import type { ChatObjectNavigationTarget } from '../../hitl-types';
import { toRecord } from '../shared/network-tool-card-utils';
import type { GroupBucket, ParsedQuery } from './data-query-parse.utils';

const OBJECT_ID_HEX = /^[a-f0-9]{24}$/i;

function formatDateBucket({
  iso,
  dateBucket,
  weekOf,
}: {
  iso: string;
  dateBucket?: string;
  weekOf: (date: string) => string;
}): string {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  if (dateBucket === 'month') {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    });
  }

  const day = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

  return dateBucket === 'week' ? weekOf(day) : day;
}

function extractObjectId(key: unknown): string | undefined {
  if (typeof key === 'string') {
    return OBJECT_ID_HEX.test(key) ? key : undefined;
  }

  const oid = toRecord(key)?.['$oid'];

  return typeof oid === 'string' ? oid : undefined;
}

export function formatBucketKey({
  key,
  dateBucket,
  noneLabel,
  weekOf,
}: {
  key: unknown;
  dateBucket?: string;
  noneLabel: string;
  weekOf: (date: string) => string;
}): string {
  if (key === null || key === undefined) {
    return noneLabel;
  }

  if (typeof key === 'object') {
    const record = toRecord(key);
    const dateValue = record?.['$date'];

    if (typeof dateValue === 'string') {
      return formatDateBucket({ iso: dateValue, dateBucket, weekOf });
    }

    const oid = record?.['$oid'];

    if (typeof oid === 'string') {
      return oid;
    }

    try {
      return JSON.stringify(key);
    } catch {
      return String(key);
    }
  }

  return String(key);
}

export function buildNavTarget(
  groupBy: ParsedQuery['groupBy'],
  group: GroupBucket,
): ChatObjectNavigationTarget | null {
  if (!groupBy) {
    return null;
  }

  const id = extractObjectId(group.key);

  if (!id) {
    return null;
  }

  switch (groupBy.field) {
    case 'endpointId':
      return {
        type: 'endpoint',
        id,
        label: group.label,
        endpointUrl: group.label,
      };
    case 'signalId':
      return { type: 'signal', id, label: group.label };
    case 'findingId':
      return { type: 'finding', id, label: group.label };
    default:
      return null;
  }
}
